/**
 * fetchSecondaryMetrics.js
 * ---------------------------------------------------------------------
 * Fetches "political" secondary metric data for both the current
 * geographic level and its parent/top-level equivalent.
 *
 * ---------------------------------------------------------------------
 * 🧭 OVERVIEW
 *
 * 1️⃣ Determines which variables to request (based on active group)
 * 2️⃣ Fetches `/api/${level}_political_data_arrow/` using Arrow IPC
 * 3️⃣ Determines and fetches the top-level dataset:
 *     - If level = "ward" → top-level = "constituency"
 *     - Else              → top-level = "national"
 * 4️⃣ Parses both Arrow tables into JavaScript objects
 * 5️⃣ Updates component state with:
 *       - SecondarymetricRows          (for current level)
 *       - SecondarymetricRowstoplevel  (for top level)
 * 6️⃣ Handles errors and logs useful debug info
 *
 * ---------------------------------------------------------------------
 * @param {Object} params
 * @param {string} params.level - Geographic level ("ward", "constituency", etc.)
 * @param {string} [params.areaCode] - Optional specific area code
 * @param {Object} params.props - Component props (for theme, etc.)
 * @param {Function} params.setState - setState updater from component
 * @param {Function} params.getActiveGroup - Returns currently active group
 * @param {Function} params.tableFromIPC - Apache Arrow helper
 * @param {Function} params.rowsFromTable - Converts Arrow Table → JS rows
 */

export async function fetchSecondaryMetrics({
  level,
  areaCode,
  props,
  setState,
  getActiveGroup,
  tableFromIPC,
  rowsFromTable,
}) {
  const group = getActiveGroup();
  if (!group || !group.keys?.length) {
    setState({
      SecondarymetricRows: null,
      SecondarymetricRowstoplevel: null,
    });
    return;
  }

  // ------------------------------------------------------------
  // 🧩 Internal helper: fetch and parse Arrow data
  // ------------------------------------------------------------
  async function fetchArrowData(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`fetchArrowData failed ${res.status}`);

    const buf = await res.arrayBuffer();
    const table = tableFromIPC(buf);
    return rowsFromTable(table);
  }

  // ------------------------------------------------------------
  // 1️⃣ Begin loading state
  // ------------------------------------------------------------
  setState({ loading: true });

  try {
    // ------------------------------------------------------------
    // 2️⃣ Fetch current-level political data
    // ------------------------------------------------------------
    const currentUrl = `/api/${level}_political_data_arrow/`;
    const requestBody = { keys: group.keys, areaCode };

    const rowsSecondary = await fetchArrowData(currentUrl, requestBody);

    // ------------------------------------------------------------
    // 3️⃣ Determine top-level and fetch it
    // ------------------------------------------------------------
    const toplevel = level === "ward" ? "constituency" : "national";
    const topUrl = `/api/${toplevel}_political_data_arrow/`;

    const rowsSecondaryTop = await fetchArrowData(topUrl, requestBody);

    // ------------------------------------------------------------
    // 4️⃣ Update state with both result sets
    // ------------------------------------------------------------
    setState({
      SecondarymetricRows: rowsSecondary,
      SecondarymetricRowstoplevel: rowsSecondaryTop,
      loading: false,
    });
  } catch (e) {
    // ------------------------------------------------------------
    // 5️⃣ Handle network or parse errors gracefully
    // ------------------------------------------------------------
    if (e.name !== "AbortError") console.error("secondary metrics error:", e);
    setState({
      SecondarymetricRows: null,
      SecondarymetricRowstoplevel: null,
      loading: false,
    });
  }

  console.log("[MapPage] fetchSecondaryMetrics", {
    level,
    areaCode,
    keys: group.keys,
  });
}
